import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'
import { 
  UserGroupIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

interface Client {
  id: string
  user_id: string
  first_name: string
  last_name: string
  email: string
  company_name: string
  phone: string
  created_at: string
  updated_at: string
}

const ClientPortalsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [inviteLoading, setInviteLoading] = useState<string | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [userStatus, setUserStatus] = useState<{ [email: string]: string }>({})

  useEffect(() => {
    if (user) {
      fetchClients()
    }
  }, [user])

  useEffect(() => {
    const fetchUserStatus = async () => {
      if (!supabase || clients.length === 0) return
      const { data: users } = await supabase.auth.admin.listUsers()
      const statusMap: { [email: string]: string } = {}
      clients.forEach((client) => {
        const user = users?.users.find((u: any) => u.email === client.email)
        if (user) {
          statusMap[client.email] = user.confirmed_at ? 'Active' : 'Invited'
        } else {
          statusMap[client.email] = 'Not Invited'
        }
      })
      setUserStatus(statusMap)
    }
    fetchUserStatus()
  }, [clients])

  const fetchClients = async () => {
    if (!supabase) {
      console.error('Supabase client is not initialized')
      return
    }

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setClients(data || [])
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredClients = clients.filter(client => 
    (client.first_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.last_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleViewPortal = (clientId: string) => {
    // Navigate to client portal view
    navigate(`/client-portals/${clientId}`)
  }

  const handleEditClient = (clientId: string) => {
    // Navigate to edit client page
    navigate(`/clients/${clientId}/edit`)
  }

  const handleDeleteClient = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return
    }

    if (!supabase) {
      console.error('Supabase client is not initialized')
      return
    }

    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId)

      if (error) throw error
      await fetchClients()
    } catch (error) {
      console.error('Error deleting client:', error)
      alert('Failed to delete client. Please try again.')
    }
  }

  const handleInvite = async (client: Client) => {
    if (!supabase) return;
    setInviteLoading(client.id)
    setInviteError(null)
    try {
      const { error } = await supabase.auth.admin.inviteUserByEmail(client.email)
      if (error) throw error
      // Refresh user status
      const { data: users } = await supabase.auth.admin.listUsers()
      const user = users?.users.find((u: any) => u.email === client.email)
      setUserStatus((prev) => ({ ...prev, [client.email]: user?.confirmed_at ? 'Active' : 'Invited' }))
    } catch (err: any) {
      setInviteError(err.message || 'Failed to send invite')
    } finally {
      setInviteLoading(null)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Client Portals
              </h1>
              <p className="text-gray-400">
                Manage your clients' portal access and settings
              </p>
            </div>
            <button
              onClick={() => navigate('/add-client')}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add New Client
            </button>
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <UserGroupIcon className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </motion.div>

        {/* Clients List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {loading ? (
            // Loading skeleton
            Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="glass-mockup rounded-xl p-6 animate-pulse"
              >
                <div className="h-6 bg-white/10 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-white/10 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-white/10 rounded w-2/3"></div>
              </div>
            ))
          ) : filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-mockup rounded-xl p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {client.first_name} {client.last_name}
                    </h3>
                    {client.company_name && (
                      <p className="text-gray-400 text-sm">{client.company_name}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewPortal(client.id)}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title="View Portal"
                    >
                      <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEditClient(client.id)}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title="Edit Client"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.id)}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete Client"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm flex items-center">
                    <span className="w-20">Email:</span>
                    <span className="text-white">{client.email}</span>
                  </p>
                  {client.phone && (
                    <p className="text-gray-400 text-sm flex items-center">
                      <span className="w-20">Phone:</span>
                      <span className="text-white">{client.phone}</span>
                    </p>
                  )}
                  <p className="text-gray-400 text-sm flex items-center">
                    <span className="w-20">Joined:</span>
                    <span className="text-white">
                      {new Date(client.created_at).toLocaleDateString()}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  <span className={`text-xs font-semibold ${userStatus[client.email] === 'Active' ? 'text-green-400' : userStatus[client.email] === 'Invited' ? 'text-yellow-400' : 'text-gray-400'}`}>Status: {userStatus[client.email] || 'Unknown'}</span>
                  {(userStatus[client.email] === 'Not Invited' || userStatus[client.email] === 'Invited') && (
                    <button
                      onClick={() => handleInvite(client)}
                      disabled={inviteLoading === client.id}
                      className="bg-blue-500 px-3 py-1 rounded-lg text-white text-xs font-semibold hover:bg-blue-600 transition"
                    >
                      {inviteLoading === client.id ? 'Sending...' : userStatus[client.email] === 'Invited' ? 'Resend Invite' : 'Invite'}
                    </button>
                  )}
                  {inviteError && <span className="text-xs text-red-400">{inviteError}</span>}
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleViewPortal(client.id)}
                    className="w-full flex items-center justify-center px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                  >
                    <EyeIcon className="w-5 h-5 mr-2" />
                    View Portal
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No clients found</h3>
              <p className="text-gray-400">
                {searchQuery
                  ? 'No clients match your search criteria'
                  : 'Get started by adding your first client'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate('/add-client')}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add New Client
                </button>
              )}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  )
}

export default ClientPortalsPage 